import { memo, useState, useCallback, useEffect } from 'react';
import { ScrollView, View, Pressable, Modal, TextInput, ActivityIndicator, Linking, RefreshControl } from 'react-native';
import { YStack, XStack, H4, H6, Button, SizableText, Text, Card } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Animatable from 'react-native-animatable';
import { Platform } from 'react-native';
import apis from '@api/api';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface CompetitorAnalysisTabProps {
  theme: boolean;
}

interface Competitor {
  _id: string;
  display_name: string;
  website_domain?: string;
  hq_country?: string;
  industry?: string;
  aliases?: string[];
  discoveryStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  lastDiscoveryAt?: string;
  discoveryError?: string;
  profileStats?: {
    total: number;
    confirmed: number;
    candidates: number;
  };
}

interface SocialProfile {
  _id: string;
  network: string;
  url: string;
  handle?: string;
  status: 'candidate' | 'confirmed' | 'rejected';
  confidence: number;
  evidence: {
    found_on_site?: boolean;
    page_url?: string;
    snippet?: string;
    rationale?: string;
    verified_badge?: boolean;
    links_back?: boolean;
  };
}

const networkIcons: Record<string, string> = {
  linkedin: 'linkedin',
  twitter: 'twitter',
  x: 'x',
  instagram: 'instagram',
  facebook: 'facebook',
  tiktok: 'tiktok',
  youtube: 'youtube',
  github: 'github'
};

const networkColors: Record<string, { light: string; dark: string }> = {
  linkedin: { light: '#0077b5', dark: '#0077b5' },
  twitter: { light: '#1DA1F2', dark: '#1DA1F2' },
  x: { light: '#000000', dark: '#ffffff' },
  instagram: { light: '#E4405F', dark: '#E4405F' },
  facebook: { light: '#1877F2', dark: '#1877F2' },
  tiktok: { light: '#000000', dark: '#ffffff' },
  youtube: { light: '#FF0000', dark: '#FF0000' },
  github: { light: '#181717', dark: '#ffffff' }
};

const CompetitorAnalysisTab = memo(({ theme }: CompetitorAnalysisTabProps) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null);
  const [showProfiles, setShowProfiles] = useState<string | null>(null);
  const [showInsights, setShowInsights] = useState<string | null>(null);
  const [analyzingCompetitorId, setAnalyzingCompetitorId] = useState<string | null>(null);
  const [newCompetitor, setNewCompetitor] = useState({
    display_name: '',
    website_domain: '',
    hq_country: '',
    industry: '',
    aliases: ''
  });

  const queryClient = useQueryClient();

  // Fetch competitors
  const { data: competitors = [], isLoading, refetch } = useQuery({
    queryKey: ['competitors'],
    queryFn: async () => {
      const response = await apis.getCompetitors();
      const competitorsList = response?.data || [];

      // Log competitor IDs for debugging
      if (competitorsList.length > 0) {
        console.log('📋 Competitors loaded:', competitorsList.map(c => ({
          _id: c._id,
          display_name: c.display_name,
          idType: typeof c._id,
          isValid: /^[0-9a-fA-F]{24}$/.test(c._id || '')
        })));
      }

      return competitorsList;
    },
    refetchInterval: (query) => {
      // Refetch if any competitor is pending or in progress
      const data = query.state.data as Competitor[];
      const hasActiveDiscovery = data?.some(c =>
        c.discoveryStatus === 'in_progress' || c.discoveryStatus === 'pending'
      );
      return hasActiveDiscovery ? 5000 : false; // Poll every 5s if discovery is active
    }
  });

  // Fetch suggested competitors
  const { data: suggestedCompetitors = [], isLoading: isLoadingSuggestions } = useQuery({
    queryKey: ['suggested-competitors'],
    queryFn: async () => {
      try {
        const response = await apis.getSuggestedCompetitors();
        return response?.data || [];
      } catch (error) {
        // If endpoint doesn't exist yet, return mock data for testing
        console.log('Suggested competitors endpoint not available, using mock data:', error);
        // Mock data for testing - remove when backend endpoint is ready
        return [
          {
            _id: 'mock-1',
            display_name: 'Salesforce',
            website_domain: 'salesforce.com',
            industry: 'CRM Software',
            hq_country: 'United States'
          },
          {
            _id: 'mock-2',
            display_name: 'HubSpot',
            website_domain: 'hubspot.com',
            industry: 'Marketing Automation',
            hq_country: 'United States'
          },
          {
            _id: 'mock-3',
            display_name: 'Microsoft',
            website_domain: 'microsoft.com',
            industry: 'Technology',
            hq_country: 'United States'
          }
        ];
      }
    },
    enabled: true, // Always fetch suggestions
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch profiles for selected competitor
  const { data: profilesData } = useQuery({
    queryKey: ['competitor-profiles', selectedCompetitor?._id],
    queryFn: async () => {
      if (!selectedCompetitor?._id) return null;

      // Validate that the ID is a valid MongoDB ObjectId format
      const id = selectedCompetitor._id;
      const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(id);
      if (!isValidObjectId) {
        console.error('Invalid competitor ID format:', id);
        return { profiles: [], profilesByNetwork: {}, stats: {} };
      }

      try {
        const response = await apis.getCompetitorSocial(id, undefined);
        return response?.data || { profiles: [], profilesByNetwork: {}, stats: {} };
      } catch (error: any) {
        console.error('Error fetching competitor social profiles:', error);
        // Return empty data on error instead of throwing
        return { profiles: [], profilesByNetwork: {}, stats: {} };
      }
    },
    enabled: !!selectedCompetitor?._id && /^[0-9a-fA-F]{24}$/.test(selectedCompetitor._id)
  });

  // Fetch insights for competitor
  const { data: insightsData } = useQuery({
    queryKey: ['competitor-insights', showInsights],
    queryFn: async () => {
      if (!showInsights || !/^[0-9a-fA-F]{24}$/.test(showInsights)) return null;
      try {
        const response = await apis.getCompetitorInsights(showInsights);
        return response?.data || { insights: [], insightsByType: {}, totalInsights: 0 };
      } catch (error: any) {
        console.error('Error fetching competitor insights:', error);
        return { insights: [], insightsByType: {}, totalInsights: 0 };
      }
    },
    enabled: !!showInsights && /^[0-9a-fA-F]{24}$/.test(showInsights)
  });

  // Fetch strategy for competitor
  const { data: strategyData } = useQuery({
    queryKey: ['competitor-strategy', showInsights],
    queryFn: async () => {
      if (!showInsights || !/^[0-9a-fA-F]{24}$/.test(showInsights)) return null;
      try {
        const response = await apis.getCompetitorStrategy(showInsights);
        return response?.data || { strategies: [], strategiesByType: {}, totalStrategies: 0 };
      } catch (error: any) {
        console.error('Error fetching competitor strategy:', error);
        return { strategies: [], strategiesByType: {}, totalStrategies: 0 };
      }
    },
    enabled: !!showInsights && /^[0-9a-fA-F]{24}$/.test(showInsights)
  });

  // Create competitor mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apis.createCompetitor(data);
      return response;
    },
    onSuccess: async (response) => {
      // Optimistically update the cache with the new competitor
      if (response?.data) {
        queryClient.setQueryData(['competitors'], (oldData: Competitor[]) => {
          const existing = oldData || [];
          // Ensure the new competitor has profileStats structure
          const newCompetitor: Competitor = {
            ...response.data,
            profileStats: response.data.profileStats || {
              total: 0,
              confirmed: 0,
              candidates: 0
            }
          };
          return [newCompetitor, ...existing];
        });
      }

      setShowAddModal(false);
      setNewCompetitor({
        display_name: '',
        website_domain: '',
        hq_country: '',
        industry: '',
        aliases: ''
      });

      // Invalidate and refetch to ensure we have the latest data from server
      // Use a small delay to ensure backend has processed the creation
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['competitors'] });
        refetch();
      }, 300);
    },
    onError: (error) => {
      console.error('Error creating competitor:', error);
      // You could add a toast notification here
    }
  });

  // Delete competitor mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apis.deleteCompetitor(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitors'] });
      if (selectedCompetitor?._id) {
        setSelectedCompetitor(null);
      }
    }
  });

  // Analyze content mutation
  const analyzeMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!id || !/^[0-9a-fA-F]{24}$/.test(id)) {
        throw new Error(`Invalid competitor ID: ${id}`);
      }
      console.log('📊 Analyzing content for competitor:', id);
      const response = await apis.analyzeCompetitorContent(id);
      return { ...response, competitorId: id };
    },
    onMutate: async (id: string) => {
      setAnalyzingCompetitorId(id);
    },
    onSuccess: (data, id) => {
      console.log(`✅ Analysis mutation successful for ${id}`);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['competitor-insights', id] });
        queryClient.invalidateQueries({ queryKey: ['competitor-strategy', id] });
        setAnalyzingCompetitorId(null);
      }, 2000);
    },
    onError: (error: any, id) => {
      console.error('❌ Error analyzing competitor content:', error);
      setAnalyzingCompetitorId(null);
    }
  });

  // Discover social profiles mutation
  const discoverMutation = useMutation({
    mutationFn: async (id: string) => {
      // Validate ID before making the request
      if (!id || !/^[0-9a-fA-F]{24}$/.test(id)) {
        throw new Error(`Invalid competitor ID: ${id}`);
      }
      console.log('🔍 Discovering social profiles for competitor:', id);
      const response = await apis.discoverCompetitorSocial(id);
      return { ...response, competitorId: id };
    },
    onMutate: async (id: string) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['competitors'] });

      // Snapshot the previous value
      const previousCompetitors = queryClient.getQueryData<Competitor[]>(['competitors']);

      // Optimistically update to pending status
      queryClient.setQueryData<Competitor[]>(['competitors'], (old) => {
        if (!old) return old;
        return old.map(competitor =>
          competitor._id === id
            ? { ...competitor, discoveryStatus: 'pending' as const, lastDiscoveryAt: new Date().toISOString() }
            : competitor
        );
      });

      return { previousCompetitors };
    },
    onSuccess: (data, id) => {
      console.log('✅ Discovery job queued successfully for competitor:', id);
      // Don't invalidate immediately - the optimistic update already shows "pending"
      // The refetchInterval will automatically pick up status changes (pending -> in_progress -> completed)
      // Only invalidate after a delay to let the backend process the job queue
      // The refetchInterval (every 5s) will handle status updates automatically
      // We only invalidate here to ensure we get the latest status after backend processes
      setTimeout(() => {
        console.log('🔄 Invalidating competitors query after discovery start');
        queryClient.invalidateQueries({ queryKey: ['competitors'] });
      }, 3000); // Wait 3 seconds for backend job to start and update status to in_progress

      // Don't invalidate competitor-profiles yet - wait until discovery completes (via websocket notification)
    },
    onError: (error: any, id, context) => {
      // Rollback optimistic update on error
      if (context?.previousCompetitors) {
        queryClient.setQueryData(['competitors'], context.previousCompetitors);
      }
      console.error('❌ Error discovering competitor social profiles:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error';
      console.error('Error details:', {
        message: errorMessage,
        status: error?.response?.status,
        data: error?.response?.data
      });
    }
  });

  // Track which competitor is currently being discovered
  const [discoveringCompetitorId, setDiscoveringCompetitorId] = useState<string | null>(null);

  // Confirm/reject profile mutation
  const confirmMutation = useMutation({
    mutationFn: async ({ competitorId, profileId, status }: { competitorId: string; profileId: string; status: 'confirmed' | 'rejected' }) => {
      return await apis.confirmCompetitorSocial(competitorId, { profileId, status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitor-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['competitors'] });
    }
  });

  const handleAddCompetitor = useCallback(() => {
    if (!newCompetitor.display_name.trim()) {
      return;
    }

    const aliasesArray = newCompetitor.aliases
      ? newCompetitor.aliases.split(',').map(a => a.trim()).filter(a => a)
      : [];

    console.log('Adding competitor:', {
      display_name: newCompetitor.display_name,
      website_domain: newCompetitor.website_domain,
      hq_country: newCompetitor.hq_country,
      industry: newCompetitor.industry,
      aliases: aliasesArray
    });

    createMutation.mutate({
      display_name: newCompetitor.display_name.trim(),
      website_domain: newCompetitor.website_domain.trim() || undefined,
      hq_country: newCompetitor.hq_country.trim() || undefined,
      industry: newCompetitor.industry.trim() || undefined,
      aliases: aliasesArray
    });
  }, [newCompetitor, createMutation]);

  const handleDeleteCompetitor = useCallback((id: string) => {
    deleteMutation.mutate(id);
  }, [deleteMutation]);

  const handleDiscover = useCallback((id: string) => {
    // Validate that the ID is a valid MongoDB ObjectId format
    const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(id);
    if (!isValidObjectId) {
      console.error('Invalid competitor ID format:', id);
      return;
    }

    // Prevent duplicate requests
    if (discoverMutation.isPending || discoveringCompetitorId === id) {
      return;
    }

    // Check if competitor is already in progress or pending
    const competitor = competitors.find(c => c._id === id);
    if (competitor?.discoveryStatus === 'in_progress' || competitor?.discoveryStatus === 'pending') {
      return;
    }

    setDiscoveringCompetitorId(id);
    discoverMutation.mutate(id, {
      onSettled: () => {
        // Clear the tracking after mutation completes (success or error)
        setDiscoveringCompetitorId(null);
      },
      onError: (error: any) => {
        console.error('Error discovering competitor social profiles:', error);
        setDiscoveringCompetitorId(null);
      }
    });
  }, [discoverMutation, discoveringCompetitorId, competitors]);

  const handleConfirmProfile = useCallback((competitorId: string, profileId: string, status: 'confirmed' | 'rejected') => {
    confirmMutation.mutate({ competitorId, profileId, status });
  }, [confirmMutation]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: theme ? '#64748b' : '#94a3b8', icon: 'schedule', label: 'Pending' },
      in_progress: { color: theme ? '#2196f3' : '#64b5f6', icon: 'sync', label: 'Discovering...' },
      completed: { color: theme ? '#4caf50' : '#81c784', icon: 'check-circle', label: 'Complete' },
      failed: { color: theme ? '#ef5350' : '#e57373', icon: 'error', label: 'Failed' }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <XStack gap={6} alignItems="center">
        <MaterialIcons name={config.icon as any} size={16} color={config.color} />
        <SizableText style={{ color: config.color, fontSize: 12, fontWeight: '600' }}>
          {config.label}
        </SizableText>
      </XStack>
    );
  };

  const getConfidenceBadge = (confidence: number) => {
    let color, label;
    if (confidence >= 90) {
      color = theme ? '#4caf50' : '#81c784';
      label = 'Likely official';
    } else if (confidence >= 55) {
      color = theme ? '#ff9800' : '#ffb74d';
      label = 'Possible match';
    } else {
      color = theme ? '#64748b' : '#94a3b8';
      label = 'Needs review';
    }

    return (
      <View style={{
        backgroundColor: color + '20',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8
      }}>
        <SizableText style={{ color, fontSize: 11, fontWeight: '600' }}>
          {label} ({confidence}%)
        </SizableText>
      </View>
    );
  };

  const profiles = profilesData?.profiles || [];
  const profilesByNetwork = profilesData?.profilesByNetwork || {};
  const stats = profilesData?.stats || {};

  return (
    <View style={{ flex: 1 }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 20, gap: 20, paddingBottom: 100 }}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={refetch}
          />
        }
      >
        <YStack gap={20}>
          {/* Header */}
          <XStack justifyContent="space-between" alignItems="center">
            <H4 style={{ color: theme ? "#1e293b" : "#f1f5f9", fontWeight: '700' }}>
              Competitor Analysis
            </H4>
            <Pressable
              onPress={() => setShowAddModal(true)}
              style={{
                backgroundColor: theme ? "#3b82f6" : "#60a5fa",
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 8,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8
              }}
            >
              <MaterialIcons name="add" size={20} color="white" />
              <SizableText style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                Add Competitor
              </SizableText>
            </Pressable>
          </XStack>

          {/* Discovered Competitors Section */}
          <YStack gap={12}>
            <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9", fontWeight: '600', fontSize: 16 }}>
              Discovered Competitors
            </H6>
            {isLoadingSuggestions ? (
              <View style={{
                padding: 20,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme ? "#f8fafc" : "#0f172a",
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? "#e2e8f0" : "#334155"
              }}>
                <ActivityIndicator size="small" color={theme ? "#3b82f6" : "#60a5fa"} />
                <SizableText style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  fontSize: 12,
                  marginTop: 8
                }}>
                  Loading suggestions...
                </SizableText>
              </View>
            ) : suggestedCompetitors.length > 0 ? (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 12, paddingRight: 20 }}
              >
                {suggestedCompetitors.map((suggestion: any) => {
                  // Check if this suggestion is already in the competitors list
                  const alreadyAdded = competitors.some(c =>
                    c.display_name.toLowerCase() === suggestion.display_name?.toLowerCase() ||
                    c.website_domain === suggestion.website_domain
                  );

                  return (
                    <Pressable
                      key={suggestion._id || suggestion.display_name}
                      onPress={() => {
                        if (!alreadyAdded) {
                          createMutation.mutate({
                            display_name: suggestion.display_name,
                            website_domain: suggestion.website_domain,
                            hq_country: suggestion.hq_country,
                            industry: suggestion.industry,
                            aliases: suggestion.aliases || []
                          });
                        }
                      }}
                      disabled={alreadyAdded || createMutation.isPending}
                      style={{
                        backgroundColor: theme ? "#ffffff" : "#1e293b",
                        borderRadius: 12,
                        padding: 16,
                        minWidth: 200,
                        maxWidth: 250,
                        borderWidth: 1,
                        borderColor: alreadyAdded
                          ? (theme ? "#94a3b8" : "#475569")
                          : (theme ? "#e2e8f0" : "#334155"),
                        opacity: alreadyAdded ? 0.6 : 1,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.1,
                        shadowRadius: 4,
                        elevation: 3,
                      }}
                    >
                      <YStack gap={8}>
                        <XStack justifyContent="space-between" alignItems="center">
                          <SizableText
                            style={{
                              color: theme ? "#1e293b" : "#f1f5f9",
                              fontWeight: '700',
                              fontSize: 14,
                              flex: 1
                            }}
                            numberOfLines={1}
                          >
                            {suggestion.display_name}
                          </SizableText>
                          {alreadyAdded && (
                            <MaterialIcons
                              name="check-circle"
                              size={20}
                              color={theme ? "#4caf50" : "#81c784"}
                            />
                          )}
                        </XStack>
                        {suggestion.website_domain && (
                          <SizableText
                            style={{
                              color: theme ? "#64748b" : "#94a3b8",
                              fontSize: 12
                            }}
                            numberOfLines={1}
                          >
                            {suggestion.website_domain}
                          </SizableText>
                        )}
                        {suggestion.industry && (
                          <View style={{
                            backgroundColor: theme ? "#f1f5f9" : "#334155",
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 6,
                            alignSelf: 'flex-start'
                          }}>
                            <SizableText style={{
                              color: theme ? "#64748b" : "#94a3b8",
                              fontSize: 11
                            }}>
                              {suggestion.industry}
                            </SizableText>
                          </View>
                        )}
                        {!alreadyAdded && (
                          <XStack
                            alignItems="center"
                            gap={6}
                            marginTop={4}
                          >
                            <MaterialIcons
                              name="add-circle-outline"
                              size={16}
                              color={theme ? "#3b82f6" : "#60a5fa"}
                            />
                            <SizableText style={{
                              color: theme ? "#3b82f6" : "#60a5fa",
                              fontSize: 12,
                              fontWeight: '600'
                            }}>
                              Add to list
                            </SizableText>
                          </XStack>
                        )}
                      </YStack>
                    </Pressable>
                  );
                })}
              </ScrollView>
            ) : (
              <View style={{
                padding: 20,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme ? "#f8fafc" : "#0f172a",
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? "#e2e8f0" : "#334155"
              }}>
                <MaterialIcons
                  name="lightbulb-outline"
                  size={32}
                  color={theme ? "#64748b" : "#94a3b8"}
                  style={{ marginBottom: 8 }}
                />
                <SizableText style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  fontSize: 12,
                  textAlign: 'center'
                }}>
                  No suggestions available at this time
                </SizableText>
              </View>
            )}
          </YStack>

          {/* Competitors List */}
          {competitors.length === 0 ? (
            <Card style={{
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderRadius: 16,
              padding: 40,
              alignItems: 'center',
              borderWidth: 1,
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
            }}>
              <MaterialIcons
                name="business"
                size={64}
                color={theme ? "#64748b" : "#94a3b8"}
                style={{ marginBottom: 16 }}
              />
              <H6 style={{
                color: theme ? "#1e293b" : "#f1f5f9",
                marginBottom: 8,
                textAlign: 'center'
              }}>
                No Competitors Yet
              </H6>
              <SizableText style={{
                color: theme ? "#64748b" : "#94a3b8",
                textAlign: 'center',
                marginBottom: 20
              }}>
                Add your first competitor to start tracking their social media profiles
              </SizableText>
              <Pressable
                onPress={() => setShowAddModal(true)}
                style={{
                  backgroundColor: theme ? "#3b82f6" : "#60a5fa",
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                  borderRadius: 8,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <MaterialIcons name="add" size={20} color="white" />
                <SizableText style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                  Add Competitor
                </SizableText>
              </Pressable>
            </Card>
          ) : (
            <YStack gap={12}>
              {competitors.map((competitor) => (
                <Card
                  key={competitor._id}
                  style={{
                    backgroundColor: theme ? "#ffffff" : "#1e293b",
                    borderRadius: 16,
                    padding: 20,
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                  }}
                >
                  <XStack justifyContent="space-between" alignItems="flex-start" marginBottom={12}>
                    <YStack flex={1}>
                      <H6 style={{
                        color: theme ? "#1e293b" : "#f1f5f9",
                        marginBottom: 4
                      }}>
                        {competitor.display_name}
                      </H6>
                      {competitor.website_domain && (
                        <SizableText style={{
                          color: theme ? "#64748b" : "#94a3b8",
                          fontSize: 12,
                          marginBottom: 4
                        }}>
                          {competitor.website_domain}
                        </SizableText>
                      )}
                      {competitor.industry && (
                        <SizableText style={{
                          color: theme ? "#64748b" : "#94a3b8",
                          fontSize: 12
                        }}>
                          {competitor.industry}
                        </SizableText>
                      )}
                    </YStack>
                    <XStack gap={8}>
                      {getStatusBadge(competitor.discoveryStatus)}
                      <Pressable
                        onPress={() => handleDeleteCompetitor(competitor._id)}
                        style={{
                          padding: 4
                        }}
                      >
                        <MaterialIcons
                          name="delete-outline"
                          size={20}
                          color={theme ? "#ef5350" : "#e57373"}
                        />
                      </Pressable>
                    </XStack>
                  </XStack>

                  {/* Profile Stats */}
                  {competitor.profileStats && (
                    <XStack gap={16} marginBottom={12}>
                      <XStack gap={4} alignItems="center">
                        <MaterialIcons
                          name="people"
                          size={16}
                          color={theme ? "#64748b" : "#94a3b8"}
                        />
                        <SizableText style={{
                          color: theme ? "#64748b" : "#94a3b8",
                          fontSize: 12
                        }}>
                          {competitor.profileStats.total} profiles
                        </SizableText>
                      </XStack>
                      <XStack gap={4} alignItems="center">
                        <MaterialIcons
                          name="check-circle"
                          size={16}
                          color={theme ? "#4caf50" : "#81c784"}
                        />
                        <SizableText style={{
                          color: theme ? "#4caf50" : "#81c784",
                          fontSize: 12
                        }}>
                          {competitor.profileStats.confirmed} confirmed
                        </SizableText>
                      </XStack>
                      {competitor.profileStats.candidates > 0 && (
                        <XStack gap={4} alignItems="center">
                          <MaterialIcons
                            name="schedule"
                            size={16}
                            color={theme ? "#ff9800" : "#ffb74d"}
                          />
                          <SizableText style={{
                            color: theme ? "#ff9800" : "#ffb74d",
                            fontSize: 12
                          }}>
                            {competitor.profileStats.candidates} candidates
                          </SizableText>
                        </XStack>
                      )}
                    </XStack>
                  )}

                  {/* Actions */}
                  <YStack gap={8}>
                    <XStack gap={8}>
                      <Button
                        flex={1}
                        onPress={() => {
                          // Validate competitor ID before setting
                          if (!competitor._id || !/^[0-9a-fA-F]{24}$/.test(competitor._id)) {
                            console.error('Invalid competitor ID when viewing profiles:', competitor._id, competitor);
                            return;
                          }
                          setSelectedCompetitor(competitor);
                          setShowProfiles(competitor._id);
                        }}
                        backgroundColor={theme ? "#f0f0f0" : "#2d3748"}
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        size="$3"
                      >
                        View Profiles
                      </Button>
                      {(competitor.discoveryStatus !== 'in_progress' && competitor.discoveryStatus !== 'pending' && discoveringCompetitorId !== competitor._id) && (
                        <Button
                          onPress={() => handleDiscover(competitor._id)}
                          backgroundColor={theme ? "#3b82f6" : "#60a5fa"}
                          color="white"
                          size="$3"
                          icon={<MaterialIcons name="search" size={16} color="white" />}
                          disabled={discoverMutation.isPending || discoveringCompetitorId !== null}
                          opacity={discoverMutation.isPending || discoveringCompetitorId !== null ? 0.5 : 1}
                        >
                          Discover
                        </Button>
                      )}
                      {(competitor.discoveryStatus === 'in_progress' || competitor.discoveryStatus === 'pending' || discoveringCompetitorId === competitor._id) && (
                        <View style={{
                          paddingHorizontal: 16,
                          paddingVertical: 8,
                          backgroundColor: theme ? "#e3f2fd" : "#0d47a1",
                          borderRadius: 8,
                          justifyContent: 'center',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8
                        }}>
                          <ActivityIndicator size="small" color={theme ? "#2196f3" : "#64b5f6"} />
                          <SizableText style={{
                            color: theme ? "#2196f3" : "#64b5f6",
                            fontSize: 12,
                            fontWeight: '600'
                          }}>
                            {competitor.discoveryStatus === 'pending' ? 'Pending...' : 'Discovering...'}
                          </SizableText>
                        </View>
                      )}
                    </XStack>
                    
                    {/* Analyze Content Button - Show if competitor has confirmed profiles */}
                    {competitor.profileStats && competitor.profileStats.confirmed > 0 && (
                      <XStack gap={8}>
                        <Button
                          flex={1}
                          onPress={() => {
                            if (!competitor._id || !/^[0-9a-fA-F]{24}$/.test(competitor._id)) {
                              console.error('Invalid competitor ID when analyzing content:', competitor._id);
                              return;
                            }
                            analyzeMutation.mutate(competitor._id);
                          }}
                          backgroundColor={theme ? "#8b5cf6" : "#a78bfa"}
                          color="white"
                          size="$3"
                          icon={<MaterialIcons name="analytics" size={16} color="white" />}
                          disabled={analyzeMutation.isPending || analyzingCompetitorId === competitor._id}
                          opacity={(analyzeMutation.isPending || analyzingCompetitorId === competitor._id) ? 0.5 : 1}
                        >
                          {analyzingCompetitorId === competitor._id ? 'Analyzing...' : 'Analyze Content'}
                        </Button>
                        <Button
                          flex={1}
                          onPress={() => {
                            if (!competitor._id || !/^[0-9a-fA-F]{24}$/.test(competitor._id)) {
                              return;
                            }
                            setShowInsights(competitor._id);
                          }}
                          backgroundColor={theme ? "#10b981" : "#34d399"}
                          color="white"
                          size="$3"
                          icon={<MaterialIcons name="insights" size={16} color="white" />}
                        >
                          View Insights
                        </Button>
                      </XStack>
                    )}
                  </YStack>
                </Card>
              ))}
            </YStack>
          )}
        </YStack>
      </ScrollView>

      {/* Add Competitor Modal */}
      <Modal
        visible={showAddModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAddModal(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'flex-end'
        }}>
          <View style={{
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            padding: 24,
            maxHeight: '90%'
          }}>
            <XStack justifyContent="space-between" alignItems="center" marginBottom={20}>
              <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9" }}>
                Add Competitor
              </H6>
              <Pressable onPress={() => setShowAddModal(false)}>
                <MaterialIcons
                  name="close"
                  size={24}
                  color={theme ? "#64748b" : "#94a3b8"}
                />
              </Pressable>
            </XStack>

            <YStack gap={16}>
              <YStack>
                <SizableText style={{
                  color: theme ? "#1e293b" : "#f1f5f9",
                  marginBottom: 8,
                  fontWeight: '600'
                }}>
                  Company Name *
                </SizableText>
                <TextInput
                  style={{
                    backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                    borderRadius: 8,
                    padding: 12,
                    color: theme ? "#1e293b" : "#f1f5f9",
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"
                  }}
                  placeholder="e.g., Acme Corp"
                  placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                  value={newCompetitor.display_name}
                  onChangeText={(text) => setNewCompetitor({ ...newCompetitor, display_name: text })}
                />
              </YStack>

              <YStack>
                <SizableText style={{
                  color: theme ? "#1e293b" : "#f1f5f9",
                  marginBottom: 8,
                  fontWeight: '600'
                }}>
                  Website Domain
                </SizableText>
                <TextInput
                  style={{
                    backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                    borderRadius: 8,
                    padding: 12,
                    color: theme ? "#1e293b" : "#f1f5f9",
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"
                  }}
                  placeholder="e.g., acme.com"
                  placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                  value={newCompetitor.website_domain}
                  onChangeText={(text) => setNewCompetitor({ ...newCompetitor, website_domain: text })}
                  autoCapitalize="none"
                />
              </YStack>

              <XStack gap={12}>
                <YStack flex={1}>
                  <SizableText style={{
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 8,
                    fontWeight: '600'
                  }}>
                    Country
                  </SizableText>
                  <TextInput
                    style={{
                      backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                      borderRadius: 8,
                      padding: 12,
                      color: theme ? "#1e293b" : "#f1f5f9",
                      borderWidth: 1,
                      borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"
                    }}
                    placeholder="e.g., USA"
                    placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    value={newCompetitor.hq_country}
                    onChangeText={(text) => setNewCompetitor({ ...newCompetitor, hq_country: text })}
                  />
                </YStack>

                <YStack flex={1}>
                  <SizableText style={{
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 8,
                    fontWeight: '600'
                  }}>
                    Industry
                  </SizableText>
                  <TextInput
                    style={{
                      backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                      borderRadius: 8,
                      padding: 12,
                      color: theme ? "#1e293b" : "#f1f5f9",
                      borderWidth: 1,
                      borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"
                    }}
                    placeholder="e.g., Technology"
                    placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    value={newCompetitor.industry}
                    onChangeText={(text) => setNewCompetitor({ ...newCompetitor, industry: text })}
                  />
                </YStack>
              </XStack>

              <YStack>
                <SizableText style={{
                  color: theme ? "#1e293b" : "#f1f5f9",
                  marginBottom: 8,
                  fontWeight: '600'
                }}>
                  Aliases (comma-separated)
                </SizableText>
                <TextInput
                  style={{
                    backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                    borderRadius: 8,
                    padding: 12,
                    color: theme ? "#1e293b" : "#f1f5f9",
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"
                  }}
                  placeholder="e.g., Acme, Acme Inc"
                  placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                  value={newCompetitor.aliases}
                  onChangeText={(text) => setNewCompetitor({ ...newCompetitor, aliases: text })}
                />
              </YStack>

              <Pressable
                onPress={handleAddCompetitor}
                disabled={!newCompetitor.display_name.trim() || createMutation.isPending}
                style={{
                  backgroundColor: (!newCompetitor.display_name.trim() || createMutation.isPending)
                    ? (theme ? "#9ca3af" : "#4b5563")
                    : (theme ? "#3b82f6" : "#60a5fa"),
                  paddingHorizontal: 20,
                  paddingVertical: 14,
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (!newCompetitor.display_name.trim() || createMutation.isPending) ? 0.6 : 1
                }}
              >
                {createMutation.isPending ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <SizableText style={{ color: 'white', fontWeight: '600', fontSize: 16 }}>
                    Add Competitor
                  </SizableText>
                )}
              </Pressable>
            </YStack>
          </View>
        </View>
      </Modal>

      {/* Profiles Modal */}
      {selectedCompetitor && showProfiles === selectedCompetitor._id && (
        <Modal
          visible={showProfiles === selectedCompetitor._id}
          animationType="slide"
          transparent={true}
          onRequestClose={() => {
            setShowProfiles(null);
            setSelectedCompetitor(null);
          }}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'flex-end'
          }}>
            <View style={{
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: 24,
              maxHeight: '90%'
            }}>
              <XStack justifyContent="space-between" alignItems="center" marginBottom={20}>
                <YStack flex={1}>
                  <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9" }}>
                    {selectedCompetitor.display_name}
                  </H6>
                  <SizableText style={{
                    color: theme ? "#64748b" : "#94a3b8",
                    fontSize: 12
                  }}>
                    {stats.total || 0} profiles • {stats.confirmed || 0} confirmed
                  </SizableText>
                </YStack>
                <Pressable onPress={() => {
                  setShowProfiles(null);
                  setSelectedCompetitor(null);
                }}>
                  <MaterialIcons
                    name="close"
                    size={24}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                </Pressable>
              </XStack>

              <ScrollView style={{ maxHeight: 600 }}>
                <YStack gap={16}>
                  {/* Confirmed Profiles */}
                  {profiles.filter(p => p.status === 'confirmed').length > 0 && (
                    <YStack>
                      <H6 style={{
                        color: theme ? "#1e293b" : "#f1f5f9",
                        marginBottom: 12
                      }}>
                        Confirmed Profiles
                      </H6>
                      <YStack gap={8}>
                        {profiles.filter(p => p.status === 'confirmed').map((profile) => (
                          <Card
                            key={profile._id}
                            style={{
                              backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                              borderRadius: 12,
                              padding: 16
                            }}
                          >
                            <XStack justifyContent="space-between" alignItems="flex-start">
                              <XStack gap={12} flex={1}>
                                <MaterialIcons
                                  name={networkIcons[profile.network] as any || 'link'}
                                  size={24}
                                  color={networkColors[profile.network]?.[theme ? 'light' : 'dark'] || (theme ? "#64748b" : "#94a3b8")}
                                />
                                <YStack flex={1}>
                                  <SizableText style={{
                                    color: theme ? "#1e293b" : "#f1f5f9",
                                    fontWeight: '600',
                                    marginBottom: 4
                                  }}>
                                    {profile.network.charAt(0).toUpperCase() + profile.network.slice(1)}
                                  </SizableText>
                                  <Pressable onPress={() => Linking.openURL(profile.url)}>
                                    <SizableText style={{
                                      color: theme ? "#3b82f6" : "#60a5fa",
                                      fontSize: 12
                                    }}>
                                      {profile.url}
                                    </SizableText>
                                  </Pressable>
                                </YStack>
                              </XStack>
                              <MaterialIcons
                                name="check-circle"
                                size={20}
                                color={theme ? "#4caf50" : "#81c784"}
                              />
                            </XStack>
                          </Card>
                        ))}
                      </YStack>
                    </YStack>
                  )}

                  {/* Candidate Profiles */}
                  {profiles.filter(p => p.status === 'candidate').length > 0 && (
                    <YStack>
                      <H6 style={{
                        color: theme ? "#1e293b" : "#f1f5f9",
                        marginBottom: 12
                      }}>
                        Candidate Profiles
                      </H6>
                      <YStack gap={8}>
                        {profiles
                          .filter(p => p.status === 'candidate')
                          .sort((a, b) => b.confidence - a.confidence)
                          .map((profile) => (
                            <Card
                              key={profile._id}
                              style={{
                                backgroundColor: theme ? "#ffffff" : "#1e293b",
                                borderRadius: 12,
                                padding: 16,
                                borderWidth: 1,
                                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                              }}
                            >
                              <XStack justifyContent="space-between" alignItems="flex-start" marginBottom={12}>
                                <XStack gap={12} flex={1}>
                                  <MaterialIcons
                                    name={networkIcons[profile.network] as any || 'link'}
                                    size={24}
                                    color={networkColors[profile.network]?.[theme ? 'light' : 'dark'] || (theme ? "#64748b" : "#94a3b8")}
                                  />
                                  <YStack flex={1}>
                                    <SizableText style={{
                                      color: theme ? "#1e293b" : "#f1f5f9",
                                      fontWeight: '600',
                                      marginBottom: 4
                                    }}>
                                      {profile.network.charAt(0).toUpperCase() + profile.network.slice(1)}
                                    </SizableText>
                                    <Pressable onPress={() => Linking.openURL(profile.url)}>
                                      <SizableText style={{
                                        color: theme ? "#3b82f6" : "#60a5fa",
                                        fontSize: 12,
                                        marginBottom: 8
                                      }}>
                                        {profile.url}
                                      </SizableText>
                                    </Pressable>
                                    {profile.evidence?.rationale && (
                                      <SizableText style={{
                                        color: theme ? "#64748b" : "#94a3b8",
                                        fontSize: 11
                                      }}>
                                        {profile.evidence.rationale}
                                      </SizableText>
                                    )}
                                  </YStack>
                                </XStack>
                                {getConfidenceBadge(profile.confidence)}
                              </XStack>
                              <XStack gap={8}>
                                <Button
                                  flex={1}
                                  onPress={() => handleConfirmProfile(selectedCompetitor._id, profile._id, 'confirmed')}
                                  backgroundColor={theme ? "#4caf50" : "#81c784"}
                                  color="white"
                                  size="$3"
                                  disabled={confirmMutation.isPending}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  flex={1}
                                  onPress={() => handleConfirmProfile(selectedCompetitor._id, profile._id, 'rejected')}
                                  backgroundColor={theme ? "#ef5350" : "#e57373"}
                                  color="white"
                                  size="$3"
                                  disabled={confirmMutation.isPending}
                                >
                                  Reject
                                </Button>
                              </XStack>
                            </Card>
                          ))}
                      </YStack>
                    </YStack>
                  )}

                  {profiles.length === 0 && (
                    <Card style={{
                      backgroundColor: theme ? "#f0f0f0" : "#2d3748",
                      borderRadius: 12,
                      padding: 40,
                      alignItems: 'center'
                    }}>
                      <MaterialIcons
                        name="search-off"
                        size={48}
                        color={theme ? "#64748b" : "#94a3b8"}
                        style={{ marginBottom: 16 }}
                      />
                      <SizableText style={{
                        color: theme ? "#64748b" : "#94a3b8",
                        textAlign: 'center'
                      }}>
                        {selectedCompetitor.discoveryStatus === 'in_progress'
                          ? 'Discovering profiles...'
                          : selectedCompetitor.discoveryStatus === 'pending'
                            ? 'Click "Discover" to find social profiles'
                            : 'No profiles found. Try adding a website domain and discovering again.'}
                      </SizableText>
                    </Card>
                  )}
                </YStack>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      {/* Insights Modal */}
      {showInsights && (
        <Modal
          visible={!!showInsights}
          animationType="slide"
          transparent={true}
          onRequestClose={() => setShowInsights(null)}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'flex-end'
          }}>
            <View style={{
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: 24,
              maxHeight: '90%'
            }}>
              <XStack justifyContent="space-between" alignItems="center" marginBottom={20}>
                <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9" }}>
                  Competitor Insights
                </H6>
                <Pressable onPress={() => setShowInsights(null)}>
                  <MaterialIcons
                    name="close"
                    size={24}
                    color={theme ? "#64748b" : "#94a3b8"}
                  />
                </Pressable>
              </XStack>

              <ScrollView style={{ maxHeight: '80%' }} showsVerticalScrollIndicator={false}>
                <YStack gap={20}>
                  {/* Insights Section */}
                  {insightsData && insightsData.totalInsights > 0 ? (
                    <YStack gap={12}>
                      <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9", fontSize: 16 }}>
                        Insights ({insightsData.totalInsights})
                      </H6>
                      {Object.entries(insightsData.insightsByType || {}).map(([type, insights]: [string, any]) => (
                        <Card
                          key={type}
                          style={{
                            backgroundColor: theme ? "#f8fafc" : "#0f172a",
                            padding: 16,
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: theme ? "#e2e8f0" : "#334155"
                          }}
                        >
                          <SizableText style={{
                            color: theme ? "#1e293b" : "#f1f5f9",
                            fontWeight: '600',
                            marginBottom: 8,
                            textTransform: 'capitalize'
                          }}>
                            {type.replace('_', ' ')}
                          </SizableText>
                          {insights.map((insight: any, idx: number) => (
                            <View key={idx} style={{ marginTop: 8 }}>
                              <SizableText style={{
                                color: theme ? "#64748b" : "#94a3b8",
                                fontSize: 12
                              }}>
                                {JSON.stringify(insight.data, null, 2).substring(0, 200)}...
                              </SizableText>
                            </View>
                          ))}
                        </Card>
                      ))}
                    </YStack>
                  ) : (
                    <View style={{
                      padding: 20,
                      alignItems: 'center',
                      backgroundColor: theme ? "#f8fafc" : "#0f172a",
                      borderRadius: 12
                    }}>
                      <MaterialIcons
                        name="insights"
                        size={32}
                        color={theme ? "#64748b" : "#94a3b8"}
                      />
                      <SizableText style={{
                        color: theme ? "#64748b" : "#94a3b8",
                        marginTop: 8,
                        textAlign: 'center'
                      }}>
                        No insights available yet. Click "Analyze Content" to generate insights.
                      </SizableText>
                    </View>
                  )}

                  {/* Strategy Section */}
                  {strategyData && strategyData.totalStrategies > 0 && (
                    <YStack gap={12}>
                      <H6 style={{ color: theme ? "#1e293b" : "#f1f5f9", fontSize: 16 }}>
                        Strategy Recommendations ({strategyData.totalStrategies})
                      </H6>
                      {strategyData.strategies.map((strategy: any, idx: number) => (
                        <Card
                          key={idx}
                          style={{
                            backgroundColor: theme ? "#f8fafc" : "#0f172a",
                            padding: 16,
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: theme ? "#e2e8f0" : "#334155"
                          }}
                        >
                          <XStack justifyContent="space-between" alignItems="center" marginBottom={8}>
                            <SizableText style={{
                              color: theme ? "#1e293b" : "#f1f5f9",
                              fontWeight: '600',
                              textTransform: 'capitalize'
                            }}>
                              {strategy.strategyType.replace('_', ' ')}
                            </SizableText>
                            <View style={{
                              backgroundColor: theme ? "#e3f2fd" : "#0d47a1",
                              paddingHorizontal: 8,
                              paddingVertical: 4,
                              borderRadius: 6
                            }}>
                              <SizableText style={{
                                color: theme ? "#2196f3" : "#64b5f6",
                                fontSize: 11,
                                fontWeight: '600'
                              }}>
                                {strategy.confidence}% confidence
                              </SizableText>
                            </View>
                          </XStack>
                          <YStack gap={4} marginTop={8}>
                            {strategy.recommendations && strategy.recommendations.map((rec: string, recIdx: number) => (
                              <XStack key={recIdx} gap={8} alignItems="flex-start">
                                <MaterialIcons
                                  name="check-circle"
                                  size={16}
                                  color={theme ? "#4caf50" : "#81c784"}
                                  style={{ marginTop: 2 }}
                                />
                                <SizableText style={{
                                  color: theme ? "#64748b" : "#94a3b8",
                                  fontSize: 12,
                                  flex: 1
                                }}>
                                  {rec}
                                </SizableText>
                              </XStack>
                            ))}
                          </YStack>
                        </Card>
                      ))}
                    </YStack>
                  )}

                  {/* Use for Content Generation Button */}
                  {insightsData && insightsData.totalInsights > 0 && (
                    <Pressable
                      onPress={() => {
                        // TODO: Navigate to content generator with competitor context
                        setShowInsights(null);
                        // You can add navigation here
                      }}
                      style={{
                        backgroundColor: theme ? "#3b82f6" : "#60a5fa",
                        padding: 16,
                        borderRadius: 12,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8
                      }}
                    >
                      <MaterialIcons name="auto-awesome" size={20} color="white" />
                      <SizableText style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                        Use for Content Generation
                      </SizableText>
                    </Pressable>
                  )}
                </YStack>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
});

CompetitorAnalysisTab.displayName = 'CompetitorAnalysisTab';

export default CompetitorAnalysisTab;

